import { Link, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight, Clock, Download, FileText, ExternalLink, BookOpen } from "lucide-react";
import { ARTICLE_BY_SLUG, ARTICLES } from "../../data/articles";
import usePageMeta from "../../lib/usePageMeta";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";

const canonicalBase = "https://ccchaps.com";

function Companion({ items }) {
  const list = Array.isArray(items) ? items : [items];
  return (
    <section className="mt-12 rounded-2xl border border-[#4A6FA5]/25 bg-[#F0F4F8] p-6 md:p-8" data-testid="article-companion">
      <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">Continue With This Resource</p>
      <div className="space-y-4">
        {list.map((c, i) => (
          <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-white border border-slate-200 p-5">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <p className="font-heading text-base text-slate-900">{c.title}</p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.desc}</p>
              </div>
            </div>
            {c.kind === "pdf" ? (
              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`companion-pdf-${i}`}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] whitespace-nowrap"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            ) : (
              <Link
                to={c.to}
                data-testid={`companion-internal-${i}`}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] whitespace-nowrap"
              >
                View Resource <ExternalLink className="w-4 h-4" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = ARTICLE_BY_SLUG[slug];
  usePageMeta({
    title: article?.metaTitle || (article ? `${article.title} | California Corporate Chaplains` : "Article | California Corporate Chaplains"),
    description: article?.metaDescription || article?.excerpt,
  });

  // Canonical + Article schema
  useEffect(() => {
    if (!article) return;
    const linkId = "canonical-link";
    let link = document.getElementById(linkId);
    if (!link) {
      link = document.createElement("link");
      link.id = linkId;
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${canonicalBase}/resources/articles/${article.slug}`;

    const schemaId = "article-schema";
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement("script");
      script.id = schemaId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.metaDescription || article.excerpt,
      articleSection: article.category,
      ...(article.publishedAt ? { datePublished: article.publishedAt } : {}),
      ...(article.heroImage?.src ? { image: `${canonicalBase}${article.heroImage.src}` } : {}),
      publisher: { "@type": "Organization", name: "California Corporate Chaplains" },
      mainEntityOfPage: `${canonicalBase}/resources/articles/${article.slug}`,
    });

    return () => {
      const l = document.getElementById(linkId);
      const s = document.getElementById(schemaId);
      if (l) l.remove();
      if (s) s.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/resources/articles" replace />;

  const related = (article.related || [])
    .map((s) => ARTICLE_BY_SLUG[s])
    .filter(Boolean)
    .slice(0, 3);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid={`article-${article.slug}`}>
      <BreadcrumbSchema
        id={`article-${article.slug}`}
        items={[
          { name: "Home", path: "/" },
          { name: "Articles & Insights", path: "/resources/articles" },
          { name: article.title, path: `/resources/articles/${article.slug}` },
        ]}
      />
      <Link to="/resources/articles" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8" data-testid="article-back">
        <ArrowLeft className="w-4 h-4" /> All Articles &amp; Insights
      </Link>

      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-[#4A6FA5]" strokeWidth={1.75} />
        <span className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">{article.category}</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1]" data-testid="article-h1">
        {article.title}
      </h1>
      <p className="mt-5 text-lg text-slate-700 leading-relaxed">{article.excerpt}</p>
      <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {article.readMins} min read</span>
        <span>·</span>
        <span>Written for {article.audience}</span>
      </div>

      {article.heroImage?.src && (
        <figure className="mt-10 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100" data-testid="article-hero-image">
          <img
            src={article.heroImage.src}
            alt={article.heroImage.alt || ""}
            loading="lazy"
            className="w-full h-auto block aspect-[16/9] object-cover"
          />
          {article.heroImage.credit && (
            <figcaption className="px-4 py-2 text-[11px] text-slate-500 bg-white">
              {article.heroImage.credit}
            </figcaption>
          )}
        </figure>
      )}

      <div className="mt-10 space-y-6" data-testid="article-body">
        {article.sections.map((s, i) => {
          if (s.callout) {
            return (
              <aside key={i} className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-5" data-testid={`article-callout-${i}`}>
                <p className="text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold mb-2">{s.callout.label}</p>
                <p className="text-sm text-slate-700 leading-relaxed">{s.callout.body}</p>
              </aside>
            );
          }
          return (
            <div key={i}>
              {s.h2 && <h2 className="font-heading text-2xl tracking-tight text-slate-900 mt-4 mb-3">{s.h2}</h2>}
              {(s.p || []).map((p, j) => (
                <p key={j} className="text-base text-slate-700 leading-relaxed mb-4 last:mb-0">
                  {Array.isArray(p)
                    ? p.map((seg, k) =>
                        typeof seg === "string" ? (
                          <span key={k}>{seg}</span>
                        ) : seg?.link ? (
                          <Link
                            key={k}
                            to={seg.link.to}
                            className="text-[#4A6FA5] hover:text-[#3B597B] font-medium underline underline-offset-2"
                          >
                            {seg.link.label}
                          </Link>
                        ) : null,
                      )
                    : p}
                </p>
              ))}
            </div>
          );
        })}
      </div>

      {article.companion && <Companion items={article.companion} />}

      {article.serviceLink && (
        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-6" data-testid="article-service-link">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Where CCCHAPS Can Help</p>
          <p className="text-sm text-slate-700 leading-relaxed">
            California Corporate Chaplains provides confidential, non-clinical, relationship-based chaplain support. Learn how our{" "}
            <Link to={article.serviceLink.to} className="text-[#4A6FA5] hover:text-[#3B597B] font-medium underline underline-offset-2">
              {article.serviceLink.label}
            </Link>{" "}
            service applies to organizations like yours.
          </p>
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-12" data-testid="article-related">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Related Articles</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/resources/articles/${r.slug}`}
                data-testid={`article-related-${r.slug}`}
                className="rounded-xl border border-slate-200 bg-white p-4 hover:border-[#4A6FA5] hover:shadow-sm transition"
              >
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">{r.category}</p>
                <p className="mt-2 font-heading text-base text-slate-900 leading-snug">{r.title}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-14 rounded-3xl bg-gradient-to-br from-[#3B597B] via-[#4A6FA5] to-[#4A6FA5] p-8 sm:p-10 text-white">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-100 font-semibold mb-2">Next Step</p>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">Want to Talk About Support for Your Organization?</h2>
        <p className="mt-3 text-blue-100 leading-relaxed max-w-2xl">
          If this is something your employees or leaders are navigating, California Corporate Chaplains can help you explore a care approach that fits your organization.
        </p>
        <Link
          to="/schedule?type=organizational_consultation"
          data-testid="article-cta-schedule"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#3B597B] font-semibold hover:bg-blue-50"
        >
          Schedule a Conversation <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </article>
  );
}
